import {
    PaymentGatewaysParameters,
    PaymentRefundFormGetRequestParameters,
    PaymentTableRecordsParameters,
    PaymentType,
    RefundMode,
    SinglePaymentParameters,
    useInvalidateRepositoryQuery,
    useRepositoryMutation,
    useRepositoryQuery,
    useRepositorySuspenseQuery,
} from '@mews-commander/data-access';
import { WithAccountId, WithCustomerId } from '@mews-commander/types';
import { lt, showSuccessAlert, useLocalizeCurrency, useLocalizeText, usePciProxyContext } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { PaymentState } from '@mews/enums';
import { alternativePaymentMethodTextKeyMap } from '@mews-commander/utils';
import { PaymentRefundFormValues } from './components/forms/PaymentRefund/schema';
import { TokenizeCardFormValues } from './components/forms/ProcessPayment/TokenizeCardForm/schema';
import { RecordCardFormValues } from './components/forms/ProcessPayment/RecordCardForm/schema';
import { formatCardExpiration } from './utils';
import { useGetPaymentMethodsRecords } from './hooks';

export const useGetPaymentRefundInitialValuesSuspense = ({ paymentId }: SinglePaymentParameters) =>
    useRepositorySuspenseQuery('paymentRefundForm', 'get', {
        paymentId,
    });

export const useGetExternalPaymentSuspense = () => useRepositorySuspenseQuery('externalPaymentForm', 'get', {});

export const useGetPaymentMethods = ({ accountId }: WithAccountId) => {
    const getPaymentMethodsRecords = useGetPaymentMethodsRecords();

    return useRepositoryQuery(
        'accountPaymentMethods',
        'getAll',
        { accountId },
        {
            select: getPaymentMethodsRecords,
        }
    );
};

export const useGetPaymentTableRecords = ({
    accountId,
    states,
}: {
    states: PaymentTableRecordsParameters['states'];
} & WithAccountId) => useRepositoryQuery('payments', 'getTableRecords', { accountId, states });

export const useGetPreauthorizationsTableRecords = ({ accountId }: WithAccountId) =>
    useRepositoryQuery('preauthorizations', 'getTableRecords', { accountId });

export const useInvalidatePreauthorizationTableRecordQuery = () => {
    const invalidateRepositoryQuery = useInvalidateRepositoryQuery();

    return ({ accountId }: WithAccountId) => {
        void invalidateRepositoryQuery('preauthorizations', 'getTableRecords', { accountId });

        void invalidateRepositoryQuery('accountChargeablePreauthorizations', 'getAll', {
            accountId,
        });
    };
};

export const useResendAuthorizationEmail = () =>
    useRepositoryMutation('paymentCardAuthorization', 'sendEmail', {
        onSuccess: () => {
            showSuccessAlert({
                description: lt('VerifyingPaymentResultMessage'),
            });
        },
    });

export const useCancelAuthorization = () =>
    useRepositoryMutation('paymentCardAuthorization', 'cancel', {
        onSuccess: () => {
            showSuccessAlert({
                description: lt('PaymentDeletedSuccessMessage'),
            });
        },
    });

export const useCancelTerminalRequest = () =>
    useRepositoryMutation('terminalRequest', 'cancel', {
        onSuccess: () => {
            showSuccessAlert({
                description: lt('PaymentDeletedSuccessMessage'),
            });
        },
    });

export const useCancelPreauthorization = ({ amount, lastFourDigists }: { amount: string; lastFourDigists: string }) =>
    useRepositoryMutation('preauthorizations', 'cancel', {
        onSuccess: () => {
            showSuccessAlert({
                description: lt('PreauthorizationCancelSuccessMessage', { amount, cardNumber: lastFourDigists }),
            });
        },
    });

export const useCreatePaymentRefund = ({
    paymentId,
    accountId,
}: {
    paymentId: string;
} & WithAccountId) => {
    const localizeText = useLocalizeText();
    const lc = useLocalizeCurrency();
    const invalidatePaymentsTableRecordsQueries = useInvalidatePaymentsTableRecordsQueries();
    const invalidatePaymentRefundFormQuery = useInvalidatePaymentRefundFormQuery();

    const transformParams = (params: PaymentRefundFormValues & { isPartialRefund: boolean }) => ({
        paymentId,
        accountId,
        reason: params.notes,
        valueToRefund: params.amount,
        paymentTerminalId: params.refundMode === RefundMode.InPerson ? params.paymentTerminalId : null,
    });
    return useRepositoryMutation('gatewayPayments', 'refund', {
        transformParams,
        onSuccess: (response, formValues) => {
            const { data, value } = response;
            const localizedAmount = lc(value.value, value.currency);
            const refundType = localizeText({
                textKey: formValues.isPartialRefund ? TextKey.PartialRefund : TextKey.FullRefund,
            });
            const isAlternativePaymentType = data.discriminator === PaymentType.Alternative;

            showSuccessAlert({
                description: lt(
                    isAlternativePaymentType
                        ? TextKey.AlternativePaymentMethodRefundSuccessMessage
                        : TextKey.PaymentRefundSuccessMessage,
                    {
                        refundType,
                        amount: localizedAmount,
                        ...(isAlternativePaymentType && {
                            method: localizeText({
                                textKey: alternativePaymentMethodTextKeyMap[data.value.data.discriminator],
                            }),
                        }),
                    }
                ),
            });

            invalidatePaymentRefundFormQuery(paymentId);
            invalidatePaymentsTableRecordsQueries({ accountId });
        },
    });
};

export const useInvalidatePaymentsTableRecordsQueries = () => {
    const invalidatePaymentsTableRecordsQuery = useInvalidatePaymentsTableRecordsQuery();
    const invalidatePaymentAttemptsTableRecordsQuery = useInvalidatePaymentAttemptsTableRecordsQuery();
    const invalidateInvoicesRecordsQuery = useInvalidateInvoicesRecordsQuery();

    return ({ accountId }: WithAccountId) => {
        invalidatePaymentsTableRecordsQuery({ accountId });
        invalidatePaymentAttemptsTableRecordsQuery({ accountId });
        invalidateInvoicesRecordsQuery({ accountId });
    };
};

export const useInvalidatePaymentsTableRecordsQuery = () => {
    const invalidateRepositoryQuery = useInvalidateRepositoryQuery();

    return ({ accountId }: WithAccountId) => {
        void invalidateRepositoryQuery('payments', 'getTableRecords', {
            accountId,
            states: [PaymentState.Charged],
        });
    };
};

export const useInvalidatePaymentAttemptsTableRecordsQuery = () => {
    const invalidateRepositoryQuery = useInvalidateRepositoryQuery();

    return ({ accountId }: WithAccountId) => {
        void invalidateRepositoryQuery('payments', 'getTableRecords', {
            accountId,
            states: [PaymentState.Canceled, PaymentState.Failed, PaymentState.Pending, PaymentState.Verifying],
        });
    };
};

export const useInvalidateInvoicesRecordsQuery = () => {
    const invalidateRepositoryQuery = useInvalidateRepositoryQuery();

    return ({ accountId }: WithAccountId) => {
        void invalidateRepositoryQuery('invoices', 'getAll', {
            ownerIds: [accountId],
        });
    };
};

export const useInvalidatePaymentMethodsTableRecordsQuery = () => {
    const invalidateRepositoryQuery = useInvalidateRepositoryQuery();

    return ({ customerId, isPreauthorization = false }: WithCustomerId & { isPreauthorization?: boolean }) => {
        void invalidateRepositoryQuery('accountPaymentMethods', 'getAll', {
            accountId: customerId,
        });

        if (isPreauthorization) {
            void invalidateRepositoryQuery('accountChargeablePreauthorizations', 'getAll', {
                accountId: customerId,
            });
        }
    };
};

export const useInvalidatePaymentRefundFormQuery = () => {
    const invalidateRepositoryQuery = useInvalidateRepositoryQuery();

    return (paymentId: PaymentRefundFormGetRequestParameters['paymentId']) =>
        void invalidateRepositoryQuery('paymentRefundForm', 'get', {
            paymentId,
        });
};

export const useTokenizeCard = ({ accountId }: WithAccountId) => {
    const pciProxy = usePciProxyContext();
    const { mutateAsync } = useRepositoryMutation('creditCards', 'addGatewayCard');

    return async ({ paymentCardHolderName, paymentCardExpiration, paymentCardFormat }: TokenizeCardFormValues) => {
        const expiration = formatCardExpiration(paymentCardExpiration);
        const { transactionId } = await pciProxy.tokenize();
        return await mutateAsync({
            nonce: transactionId,
            accountId,
            expiration: expiration ?? '',
            format: paymentCardFormat,
            holderName: paymentCardHolderName,
        });
    };
};

export const useRecordCard = ({ accountId }: WithAccountId) => {
    const { mutateAsync } = useRepositoryMutation('creditCards', 'addNonGatewayCard');

    return async ({
        paymentCardHolderName,
        paymentCardExpiration,
        paymentCardFormat,
        paymentCardType,
        paymentCardNumber,
    }: RecordCardFormValues) => {
        const expiration = formatCardExpiration(paymentCardExpiration);
        return await mutateAsync({
            accountId,
            expiration,
            format: paymentCardFormat,
            holderName: paymentCardHolderName,
            obfuscatedNumber: paymentCardNumber,
            type: paymentCardType,
        });
    };
};

export const useGetAccountingConfigurationOptionsSuspense = () =>
    useRepositorySuspenseQuery(
        'accountingConfigurations',
        'get',
        {},
        {
            select: (data) => data.accountingConfiguration.options,
        }
    );

export const useGetAccountingConfigurationReceivableTrackingEnabled = (enabled = true) =>
    useRepositoryQuery(
        'accountingConfigurations',
        'get',
        {},
        {
            enabled,
            select: (data) => data.accountingConfiguration.options.receivableTrackingEnabled,
            staleTime: 5000,
        }
    );

export const useGetAccountingSurchargeFeeSuspense = () =>
    useRepositorySuspenseQuery(
        'accountingConfigurations',
        'get',
        {},
        {
            select: (data) => ({
                surchargeFees: data.accountingConfiguration.surchargeFees,
                surchargeServiceId: data.accountingConfiguration.surchargeServiceId,
            }),
        }
    );

export const useAccountingGetConfigurationAcceptedManualPaymentCardTypesSuspense = () =>
    useRepositorySuspenseQuery(
        'accountingConfigurations',
        'get',
        {},
        {
            select: (data) => data.accountingConfiguration.acceptedManualCreditCardTypes,
        }
    );

export const useGetPaymentGatewaysSuspense = (data: PaymentGatewaysParameters['data']) =>
    useRepositorySuspenseQuery('paymentGateways', 'get', {
        data,
    });

export const useGetPaymentMethodsSuspense = ({ accountId }: WithAccountId) =>
    useRepositorySuspenseQuery('accountPaymentMethods', 'getAll', { accountId });
